
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Database, Download, Filter, Search, BarChart3, FileText, Eye, Trophy} from 'lucide-react'

const DatabasePage = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterImportancia, setFilterImportancia] = useState('Todas')
  const [selectedView, setSelectedView] = useState('tabla')

  // Datos ampliados de cuencas con ranking de priorización
  const cuencasData = [
    {
      id: 1,
      nombre: 'Río Grijalva',
      jerarquia: 1,
      area: 52000,
      importancia: 'Muy Alta',
      municipios: 45,
      poblacion: 2100000,
      caudal: '1,200 m³/s',
      precipitacion: '2,400 mm/año',
      region: 'Centro-Norte',
      estado: 'Activa',
      rankingPriorizacion: 1,
      indicePrioridad: 0.5134
    },
    {
      id: 2,
      nombre: 'Río Usumacinta',
      jerarquia: 2,
      area: 35000,
      importancia: 'Alta',
      municipios: 28,
      poblacion: 850000,
      caudal: '850 m³/s',
      precipitacion: '3,200 mm/año',
      region: 'Norte',
      estado: 'Activa',
      rankingPriorizacion: 2,
      indicePrioridad: 0.4892
    },
    {
      id: 3,
      nombre: 'Río Lacantún',
      jerarquia: 3,
      area: 18500,
      importancia: 'Alta',
      municipios: 12,
      poblacion: 320000,
      caudal: '420 m³/s',
      precipitacion: '2,800 mm/año',
      region: 'Selva',
      estado: 'Monitoreo',
      rankingPriorizacion: 3,
      indicePrioridad: 0.4156
    },
    {
      id: 4,
      nombre: 'Río Suchiate',
      jerarquia: 4,
      area: 8200,
      importancia: 'Media',
      municipios: 8,
      poblacion: 180000,
      caudal: '180 m³/s',
      precipitacion: '1,800 mm/año',
      region: 'Soconusco',
      estado: 'Activa',
      rankingPriorizacion: 4,
      indicePrioridad: 0.3247
    },
    {
      id: 5,
      nombre: 'Río Coatán',
      jerarquia: 5,
      area: 6800,
      importancia: 'Media',
      municipios: 6,
      poblacion: 120000,
      caudal: '120 m³/s',
      precipitacion: '2,100 mm/año',
      region: 'Costa',
      estado: 'Activa',
      rankingPriorizacion: 5,
      indicePrioridad: 0.2891
    },
    {
      id: 6,
      nombre: 'Río Zanatenco',
      jerarquia: 6,
      area: 4200,
      importancia: 'Baja',
      municipios: 4,
      poblacion: 85000,
      caudal: '75 m³/s',
      precipitacion: '1,600 mm/año',
      region: 'Sierra',
      estado: 'Monitoreo',
      rankingPriorizacion: 6,
      indicePrioridad: 0.1978
    }
  ]

  const filteredData = cuencasData.filter(cuenca => {
    const matchesSearch = cuenca.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterImportancia === 'Todas' || cuenca.importancia === filterImportancia
    return matchesSearch && matchesFilter
  })

  const estadisticas = {
    totalCuencas: cuencasData.length,
    areaTotal: cuencasData.reduce((sum, c) => sum + c.area, 0),
    poblacionTotal: cuencasData.reduce((sum, c) => sum + c.poblacion, 0),
    municipiosTotal: cuencasData.reduce((sum, c) => sum + c.municipios, 0)
  }

  const getRankingIcon = (ranking: number) => {
    if (ranking === 1) return '🥇'
    if (ranking === 2) return '🥈'
    if (ranking === 3) return '🥉'
    return ranking.toString()
  }

  const getRankingColor = (ranking: number) => {
    if (ranking === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    if (ranking === 2) return 'bg-gray-100 text-gray-800 border-gray-300'
    if (ranking === 3) return 'bg-orange-100 text-orange-800 border-orange-300'
    return 'bg-blue-100 text-blue-800 border-blue-300'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Inicio</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                  <Database className="text-white w-4 h-4" />
                </div>
                <h1 className="text-xl font-bold text-gray-800">Base de Datos de Cuencas</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                <Download className="w-4 h-4" />
                <span>Exportar</span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        {/* Estadísticas Generales */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Cuencas</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas.totalCuencas}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Área Total</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas.areaTotal.toLocaleString()}</p>
                <p className="text-gray-500 text-xs">km²</p>
              </div>
              <Database className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Población</p>
                <p className="text-3xl font-bold text-purple-600">{(estadisticas.poblacionTotal / 1000000).toFixed(1)}M</p>
                <p className="text-gray-500 text-xs">habitantes</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Municipios</p>
                <p className="text-3xl font-bold text-orange-600">{estadisticas.municipiosTotal}</p>
                <p className="text-gray-500 text-xs">total</p>
              </div>
              <Eye className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </motion.div>

        {/* Controles de Filtrado */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar cuenca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={filterImportancia}
                onChange={(e) => setFilterImportancia(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Todas">Todas las importancias</option>
                <option value="Muy Alta">Muy Alta</option>
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedView('tabla')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedView === 'tabla' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tabla
              </button>
              <button
                onClick={() => setSelectedView('tarjetas')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedView === 'tarjetas' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tarjetas
              </button>
            </div>
          </div>
        </motion.div>

        {/* Vista de Datos */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {selectedView === 'tabla' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranking</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jerarquía</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuenca</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Área (km²)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importancia</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Municipios</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Población</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caudal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Índice Prioridad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((cuenca) => (
                    <motion.tr 
                      key={cuenca.id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center justify-center w-10 h-8 border-2 rounded-full text-sm font-bold ${getRankingColor(cuenca.rankingPriorizacion)}`}>
                          {getRankingIcon(cuenca.rankingPriorizacion)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                          {cuenca.jerarquia}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{cuenca.nombre}</div>
                        <div className="text-sm text-gray-500">{cuenca.region}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.area.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          cuenca.importancia === 'Muy Alta' 
                            ? 'bg-red-100 text-red-800'
                            : cuenca.importancia === 'Alta'
                            ? 'bg-orange-100 text-orange-800'
                            : cuenca.importancia === 'Media'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {cuenca.importancia}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.municipios}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.poblacion.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.caudal}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {cuenca.indicePrioridad.toFixed(4)}
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-green-400 to-red-500 h-2 rounded-full" 
                              style={{ width: `${(cuenca.indicePrioridad / 0.5134) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          cuenca.estado === 'Activa' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {cuenca.estado}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredData.map((cuenca) => (
                <motion.div
                  key={cuenca.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-blue-300 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-full text-lg font-bold">
                        {cuenca.jerarquia}
                      </span>
                      <div className={`inline-flex items-center justify-center w-10 h-8 border-2 rounded-full text-sm font-bold ${getRankingColor(cuenca.rankingPriorizacion)}`}>
                        {getRankingIcon(cuenca.rankingPriorizacion)}
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      cuenca.importancia === 'Muy Alta' 
                        ? 'bg-red-100 text-red-800'
                        : cuenca.importancia === 'Alta'
                        ? 'bg-orange-100 text-orange-800'
                        : cuenca.importancia === 'Media'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {cuenca.importancia}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{cuenca.nombre}</h3>
                  <p className="text-sm text-gray-600 mb-4">{cuenca.region}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Área:</span>
                      <span className="font-medium">{cuenca.area.toLocaleString()} km²</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Municipios:</span>
                      <span className="font-medium">{cuenca.municipios}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Población:</span>
                      <span className="font-medium">{cuenca.poblacion.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Caudal:</span>
                      <span className="font-medium">{cuenca.caudal}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Índice Prioridad:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-xs">{cuenca.indicePrioridad.toFixed(4)}</span>
                        <div className="w-12 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-400 to-red-500 h-2 rounded-full" 
                            style={{ width: `${(cuenca.indicePrioridad / 0.5134) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default DatabasePage
